import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserData from './redux/hooks/useUserData';
import useCheckIn from './redux/hooks/useCheckIn';
import { useSurvey } from './redux/hooks/useSurvey';
import { patientApi } from '../services/api';

// Import forms dynamically
import LhcFormRenderer from './LhcFormRenderer';
import TelemedicineConsentForm from './forms/TelemedicineConsentForm';
import NonLhcFormRenderer from './forms/NonLhcFormRenderer';

/**
 * FormContent component to render the content of a single form page.
 */
const FormContent = ({ form, resolvedTokenData, handleFormSubmission, currentFormIndex, totalForms, formsList, completedForms }) => {
  // Show loading state while fetching form data
  if (form.isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading form data...</span>
        </div>
      </div>
    );
  }

  // If there's an error, show a proper error message with context
  if (form.error) {
    
    // Determine error type and show appropriate message
    const isAccessError = form.error.includes('404') ||
                         form.error.includes('not found') ||
                         form.error.includes('not attached') ||
                         form.error.includes('not authorized') ||
                         form.error.includes('access denied') ||
                         form.error.includes('Permission denied');
    
    const isNetworkError = form.error.includes('network') ||
                          form.error.includes('timeout') ||
                          form.error.includes('connection');
    
    return (
      <div className="alert alert-warning">
        <h5>
          <i className="fas fa-exclamation-triangle me-2"></i>
          Form Unavailable
        </h5>
        <p className="mb-2">
          <strong>Form ID {form.form_id}</strong>
          {form.version && ` (Version ${form.original_version || form.version}) `}
          cannot be loaded.
        </p>
        
        {isAccessError && (
          <div className="mb-3">
            <p className="mb-1"><strong>Reason:</strong> This form is not available for your health system or you don&apos;t have permission to access it.</p>
            <p className="mb-0 text-muted">It is safe to skip this form but you can let the provider know once the visit starts.</p>
          </div>
        )}
        
        {isNetworkError && (
          <div className="mb-3">
            <p className="mb-1"><strong>Reason:</strong> Network connection issue.</p>
            <p className="mb-0 text-muted">Please check your internet connection and try again.</p>
          </div>
        )}
        
        {!isAccessError && !isNetworkError && (
          <div className="mb-3">
            <p className="mb-1"><strong>Error:</strong> {form.error}</p>
            <p className="mb-0 text-muted">Please contact support if this issue persists.</p>
          </div>
        )}
        
        <div className="mt-3">
          <small className="text-muted">
            This form has been automatically skipped. You can continue with the remaining forms.
          </small>
        </div>
      </div>
    );
  }

  // Render the form if schema is available
  if (form.schema) {

    // Ensure formData is initialized even if it's null or undefined
    const initializedForm = {
      ...form,
      formData: form.formData || {}
    };

    return (
      <div className="form-content-wrapper">
        {/* Progress Indicator */}
        {totalForms > 1 && (
          <div className="form-progress mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Form Progress</h5>
              <span className="badge bg-primary">{currentFormIndex + 1} of {totalForms}</span>
            </div>
            <div className="progress mb-3">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${((currentFormIndex + 1) / totalForms) * 100}%` }}
                aria-valuenow={currentFormIndex + 1}
                aria-valuemin="0"
                aria-valuemax={totalForms}
              ></div>
            </div>
            
            {/* Form Navigation Breadcrumbs */}
            <nav aria-label="Form navigation">
              <ol className="breadcrumb">
                {formsList?.map((formItem, index) => (
                  <li
                    key={`${formItem.form_id}-${formItem.version}`}
                    className={`breadcrumb-item ${index === currentFormIndex ? 'active' : ''}`}
                  >
                    {index === currentFormIndex ? (
                      <span>Form {formItem.form_id} (v{formItem.original_version || formItem.version})</span>
                    ) : (
                      <span className={
                        completedForms.includes(`${formItem.form_id}-${formItem.original_version || formItem.version}`)
                          ? 'text-success'
                          : 'text-muted'
                      }>
                        {completedForms.includes(`${formItem.form_id}-${formItem.original_version || formItem.version}`) && <i className="fas fa-check me-1"></i>}
                        Form {formItem.form_id} (v{formItem.original_version || formItem.version})
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        )}

        {/* Form Content */}
        <div className="form-content">
          {/* Render the appropriate form component based on type */}
          {form?.type?.name === 'LHC form' ? (
            <LhcFormRenderer
              formDetail={initializedForm}
              tokenData={resolvedTokenData}
              onSuccess={handleFormSubmission}
            />
          ) : form?.type?.name === 'Non-LHC form' ? (
            <NonLhcFormRenderer
              formDetail={initializedForm}
              onSubmit={handleFormSubmission}
            />
          ) : (
            <div className="alert alert-warning">
              <h5>Unsupported Form Type</h5>
              <p>The form type &quot;{form?.type?.name}&quot; for form ID: {form?.form_id} is not supported. Please contact support.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback for missing schema
  return (
    <div className="alert alert-info">
      <h5>Form Data Debug Info</h5>
      <p>Form ID: {form?.form_id}</p>
      <p>isLoading: {form?.isLoading ? 'true' : 'false'}</p>
      <p>error: {form?.error || 'null'}</p>
      <p>schema: {form?.schema ? 'present' : 'missing'}</p>
    </div>
  );
};

/**
 * IntakeForms component for collecting patient medical information,
 * rendering one form per page based on URL parameter.
 */
const IntakeForms = ({ tokenData, onSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Component state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formDetail, setFormDetail] = useState(null);
  const [formsList, setFormsList] = useState([]);
  const [currentFormIndex, setCurrentFormIndex] = useState(-1);
  const [resolvedTokenData, setResolvedTokenData] = useState(null);
  const [skippedForms, setSkippedForms] = useState([]); // Track skipped forms
  const [completedForms, setCompletedForms] = useState([]); // Track completed forms
  const [eligibleFormsList, setEligibleFormsList] = useState([]); // New state for eligible forms list


  // Import the useUserData, useCheckIn, and useSurvey hooks
  const { updatePatientId } = useUserData();
  const { updateCurrentFormId, updateCurrentFormVersion } = useCheckIn();
  const { submitFormResponse } = useSurvey();

  // First, resolve tokenData if it's a Promise
  useEffect(() => {

    const resolveTokenData = async () => {
      try {
        if (tokenData instanceof Promise) {
          const resolved = await tokenData;
          setResolvedTokenData(resolved);
          
          // Save patient ID to Redux if available
          if (resolved && resolved.sub) {
            updatePatientId(resolved.sub);
          }
        } else {
          setResolvedTokenData(tokenData);
          
          // Save patient ID to Redux if available
          if (tokenData && tokenData.sub) {
            updatePatientId(tokenData.sub);
          }
        }
      } catch (error) {
        setError('Failed to load authentication data. Please try again.');
        setIsLoading(false);
      }
    };

    resolveTokenData();
  }, [tokenData]);

  // Extract form IDs from the JWT payload once tokenData is resolved
  useEffect(() => {
    if (!resolvedTokenData) {
      return;
    }

    // Extract form IDs from the JWT payload
    let extractedForms = [];

    if (resolvedTokenData.form_ids && Array.isArray(resolvedTokenData.form_ids)) {
      console.log('Found form_ids array in JWT payload:', resolvedTokenData.form_ids);

      // Map the form_ids array to extract form_id and version
      extractedForms = resolvedTokenData.form_ids
        .map(item => {
          if (typeof item === 'object' && item !== null) {
            // Process version if it's a string like "1.2"
            let processedVersion = item.version;
            if (typeof processedVersion === 'string' && processedVersion.includes('.')) {
              // Store the original version for display purposes
              const originalVersion = processedVersion;
              // Extract the major version number for backend queries
              const versionParts = processedVersion.split('.');
              processedVersion = parseInt(versionParts[0], 10);
            }

            return {
              form_id: item.form_id,
              version: processedVersion || null,
              original_version: item.version || null, // Keep the original version for reference
              saved_data: item.saved_data || null,
              status: item.status || null, // Extract status (although JWT uses assigned/reason)
              assigned: item.assigned, // Extract assigned boolean
              reason: item.reason // Extract reason string
            };
          }
          // Handle cases where item is just a form ID (number) - assume eligible if assigned is not explicitly false
          return { form_id: parseInt(item, 10), version: null, original_version: null, saved_data: null, status: 'assigned', assigned: true, reason: 'Implicitly assigned' };
        })
        .filter(item => {
          // Filter to include only forms where assigned is true and reason is 'Form assigned successfully.'
          const isEligible = item.assigned === true;
          if (!isEligible) {
            // console.log(`Filtering out ineligible form ID ${item.form_id} with reason: ${item.reason}`);
          }
          return !isNaN(item.form_id) && isEligible;
        });

    } else if (resolvedTokenData.form_id) {
      // Handle single form_id - assume eligible if assigned is not explicitly false
      const formId = parseInt(resolvedTokenData.form_id, 10);
      if (!isNaN(formId)) {
        // Assuming a single form_id means it's assigned
        extractedForms = [{ form_id: formId, version: null, original_version: null, saved_data: null, status: 'assigned', assigned: true, reason: 'Single form assigned' }];
      }
    }

    if (extractedForms.length === 0) {
      setError('You do not have any forms to fill out for this visit.');
      setIsLoading(false);
      return;
    }
    setFormsList(extractedForms);
    setEligibleFormsList(extractedForms); // Set the eligible forms list

    // Determine the initial form to load based on URL or the first form
    const urlParams = new URLSearchParams(location.search);
    const urlFormId = parseInt(urlParams.get('formid'), 10);
    const urlVersion = urlParams.get('version');
    
    
    // Convert urlVersion to integer for proper comparison if it exists
    const urlVersionInt = urlVersion ? parseInt(urlVersion, 10) : null;

    const initialFormIndex = extractedForms.findIndex(form => {
      const formIdMatches = form.form_id === urlFormId;
      const versionMatches = !urlVersionInt || form.version === urlVersionInt;
      return formIdMatches && versionMatches;
    });

    if (initialFormIndex !== -1) {
      setCurrentFormIndex(initialFormIndex);
    } else if (extractedForms.length > 0) {
       // If no valid formid/version in URL, start with the first form
      //  console.log('IntakeForms: No valid formid/version in URL, attempting to load the first form.');
       setCurrentFormIndex(0);
       const firstForm = extractedForms[0];
      //  console.log('IntakeForms: Setting currentFormIndex to 0 and navigating to first form:', firstForm);
       // Update URL to reflect the first form ID and version
       const newSearchParams = `?step=2&formid=${firstForm.form_id}${firstForm.original_version ? `&version=${firstForm.original_version}` : ''}`;
       console.log('IntakeForms: Navigating to first form with URL:', newSearchParams);
       navigate({
         search: newSearchParams
       }, { replace: true });
     } else {
        // If no eligible forms are found, display a success message and move to the next step
        setIsLoading(false);
        // Display a success message to the user
        setError('Yay! No forms to fill today. Proceeding to the next step...'); // Using error state to display message temporarily
        // Automatically move to the next step after a short delay
        setTimeout(() => {
          onSuccess({ skippedForms }); // Call onSuccess to move to the next step
        }, 5000); // 2-second delay before moving to the next step
     }


  }, [resolvedTokenData, location.search, navigate]);

  // Separate effect to handle URL changes and update currentFormIndex
  useEffect(() => {
    if (!formsList.length || !resolvedTokenData) return;

    const urlParams = new URLSearchParams(location.search);
    const urlFormId = parseInt(urlParams.get('formid'), 10);
    const urlVersion = urlParams.get('version');
    
    
    if (!isNaN(urlFormId)) {
      const urlVersionInt = urlVersion ? parseInt(urlVersion, 10) : null;
      const formIndex = formsList.findIndex(form => {
        const formIdMatches = form.form_id === urlFormId;
        const versionMatches = !urlVersionInt || form.version === urlVersionInt;
        return formIdMatches && versionMatches;
      });
      
      if (formIndex !== -1 && formIndex !== currentFormIndex) {
        setCurrentFormIndex(formIndex);
      }
    }
  }, [location.search, formsList, currentFormIndex, resolvedTokenData]);

  // Effect to fetch the current form's schema and data when currentFormIndex changes
  useEffect(() => {
    if (currentFormIndex === -1 || formsList.length === 0 || !resolvedTokenData) {
      return;
    }

    // Check if the current form has an error, and if so, move to the next form
    if (formDetail && formDetail.error) {
      console.log(`Current form (ID: ${formDetail.form_id}) has an error, moving to next form`);

      // If there are more forms, move to the next one
      if (currentFormIndex < formsList.length - 1) {
        const nextFormIndex = currentFormIndex + 1;
        const nextForm = formsList[nextFormIndex];
        setCurrentFormIndex(nextFormIndex);
        // Update URL to navigate to the next form, including version
        navigate({
          search: `?step=2&formid=${nextForm.form_id}${nextForm.original_version ? `&version=${nextForm.original_version}` : ''}`
        });
        return;
      } else {
        // If this is the last form and it has an error, call onSuccess to complete the process
        onSuccess({ skippedForms });
        return;
      }
    }

    const currentForm = formsList[currentFormIndex];

    const fetchCurrentFormData = async () => {
      setIsLoading(true);
      setError(null);
      setFormDetail(null); // Clear previous form detail

      const patientId = resolvedTokenData.sub;
      if (!patientId) {
        setError('Patient ID is missing. Please try again or contact support.');
        setIsLoading(false);
        return;
      }

      // Store current form ID and version in Redux
      updateCurrentFormId(currentForm.form_id);
      updateCurrentFormVersion(currentForm.version || currentForm.original_version);

      try {
        // Fetch the form definition and data for the current form ID and version
        const formDataResponse = await patientApi.getFormData(
          patientId,
          currentForm.form_id,
          null, // submissionId is not needed for fetching the latest
          currentForm.version || null,
          resolvedTokenData
        );

        if (formDataResponse?.data?.success && formDataResponse.data.schema) {

          // Always use form_values from API as the source of truth
          let savedData = formDataResponse.data.form_values || {};

          // Ensure savedData is in the correct format for LHC forms
          if (savedData && typeof savedData === 'object') {
            // If savedData is already in the expected format, use it as is
          } else {
            // If savedData is null or not an object, initialize it as an empty object
            savedData = {};
          }

          // Store backend form data in Redux if available
          if (savedData && Object.keys(savedData).length > 0) {
            submitFormResponse(currentForm.form_id, savedData);
          }

          const formDetail = {
            form_id: currentForm.form_id,
            version: currentForm.version,
            original_version: currentForm.original_version || currentForm.version,
            schema: formDataResponse.data.schema || { pages: [] },
            formData: savedData,
            submissionId: formDataResponse.data.submission_id || null,
            type: formDataResponse.data.type || { name: 'LHC form' }, // Default to LHC form if not specified
            isLoading: false,
            error: null,
            last_updated_at: currentForm.last_updated_at || null
          };
          setFormDetail(formDetail);
        } else if (formDataResponse?.data?.success && formDataResponse.data.message === "No data found for this form.") {
          // Handle case when no saved data is found - show a new form instance
          console.log(`No saved data found for form ID ${currentForm.form_id}. Creating new form instance.`);

          // Fetch the form schema without expecting saved data
          try {
            const schemaResponse = await patientApi.getFormData(
              patientId,
              currentForm.form_id,
              null,
              currentForm.version || null,
              resolvedTokenData
            );

            if (schemaResponse?.data?.success && schemaResponse.data.schema) {
              const formDetail = {
                form_id: currentForm.form_id,
                version: currentForm.version,
                original_version: currentForm.original_version || currentForm.version,
                schema: schemaResponse.data.schema || { pages: [] },
                formData: {}, // Empty form data for a new form instance
                submissionId: null, // No submission ID for a new form
                type: schemaResponse.data.type || { name: 'LHC form' },
                isLoading: false,
                error: null
              };
    
              setFormDetail(formDetail);
            } else {
              throw new Error('Failed to load form schema');
            }
          } catch (schemaError) {
            setFormDetail({
              form_id: currentForm.form_id,
              version: currentForm.version,
              original_version: currentForm.original_version || currentForm.version,
              schema: null,
              formData: null,
              submissionId: null,
              isLoading: false,
              error: 'Failed to load form schema'
            });
          }
        } else {
          console.error(`Failed to fetch data for form ID ${currentForm.form_id}:`, formDataResponse?.data?.message || 'Unknown error');

          // Add this form to the skipped forms list
          const errorMessage = formDataResponse?.data?.message || 'Failed to load form definition';
          const newSkippedForm = {
            form_id: currentForm.form_id,
            version: currentForm.version,
            original_version: currentForm.original_version || currentForm.version,
            error: errorMessage,
            timestamp: new Date().toISOString()
          };

          // Check if this form is already in the skippedForms array before adding it
          setSkippedForms(prevSkippedForms => {
            // Create a unique key for the form using form_id and version
            const formKey = `${newSkippedForm.form_id}-${newSkippedForm.original_version || newSkippedForm.version}`;

            // Check if a form with this key already exists in the array
            const formExists = prevSkippedForms.some(form =>
              `${form.form_id}-${form.original_version || form.version}` === formKey
            );

            // Only add the form if it doesn't already exist
            if (formExists) {
              return prevSkippedForms;
            }
            return [...prevSkippedForms, newSkippedForm];
          });

          // If there are more forms, move to the next one instead of showing an error
          if (currentFormIndex < formsList.length - 1) {
            const nextFormIndex = currentFormIndex + 1;
            const nextForm = formsList[nextFormIndex];
            setCurrentFormIndex(nextFormIndex);
            // Update URL to navigate to the next form, including version
            navigate({
              search: `?step=2&formid=${nextForm.form_id}${nextForm.original_version ? `&version=${nextForm.original_version}` : ''}`
            });
            return; // Exit the function to avoid setting formDetail
          } else {
            // If this is the last form and it was skipped, call onSuccess to complete the process
            if (skippedForms.length === formsList.length) {
              // All forms were skipped, call onSuccess with skipped forms data
              // Check if the new skipped form is already in the array before adding it
              const formKey = `${newSkippedForm.form_id}-${newSkippedForm.original_version || newSkippedForm.version}`;
              const formExists = skippedForms.some(form =>
                `${form.form_id}-${form.original_version || form.version}` === formKey
              );

              // Only add the form if it doesn't already exist
              const updatedSkippedForms = formExists ? skippedForms : [...skippedForms, newSkippedForm];
              onSuccess({ skippedForms: updatedSkippedForms });
              return;
            }

            // Otherwise, show the error for the last form
            setFormDetail({
              form_id: currentForm.form_id,
              version: currentForm.version,
              original_version: currentForm.original_version || currentForm.version,
              schema: null,
              formData: null,
              submissionId: null,
              isLoading: false,
              error: errorMessage
            });
          }
        }
      } catch (error) {
        // Categorize the error for better user messaging
        let errorMessage = 'An unexpected error occurred while loading the form.';
        
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          
          if (status === 404) {
            if (responseData?.message && responseData.message.includes('is not assigned to patient')) {
              errorMessage = '404 - Form not available - This form is not assigned to your health system.';
            } else {
              errorMessage = '404 - Form not found - This form may have been removed or is not available.';
            }
          } else if (status === 403) {
            errorMessage = 'Permission denied - You do not have access to this form.';
          } else if (status === 401) {
            errorMessage = 'Authentication failed - Please refresh the page and try again.';
          } else if (status >= 500) {
            errorMessage = 'Server error - Please try again later or contact support.';
          } else if (responseData?.message) {
            errorMessage = responseData.message;
          }
        } else if (error.message) {
          if (error.message.includes('Network Error') || error.message.includes('timeout')) {
            errorMessage = 'Network connection issue - Please check your internet connection.';
          } else {
            errorMessage = error.message;
          }
        }

        const newSkippedForm = {
          form_id: currentForm.form_id,
          version: currentForm.version,
          original_version: currentForm.original_version || currentForm.version,
          error: errorMessage,
          timestamp: new Date().toISOString()
        };

        setSkippedForms(prevSkippedForms => [...prevSkippedForms, newSkippedForm]);

        // If there are more forms, move to the next one instead of showing an error
        if (currentFormIndex < formsList.length - 1) {
          const nextFormIndex = currentFormIndex + 1;
          const nextForm = formsList[nextFormIndex];
          setCurrentFormIndex(nextFormIndex);
          // Update URL to navigate to the next form, including version
          navigate({
            search: `?step=2&formid=${nextForm.form_id}${nextForm.original_version ? `&version=${nextForm.original_version}` : ''}`
          });
          return; // Exit the function to avoid setting formDetail
        } else {
          // If this is the last form and it was skipped, call onSuccess to complete the process
          if (skippedForms.length === formsList.length - 1) { // -1 because we haven't added newSkippedForm yet
            // All forms were skipped, call onSuccess with skipped forms data
            // Check if the new skipped form is already in the array before adding it
            const formKey = `${newSkippedForm.form_id}-${newSkippedForm.original_version || newSkippedForm.version}`;
            const formExists = skippedForms.some(form =>
              `${form.form_id}-${form.original_version || form.version}` === formKey
            );

            // Only add the form if it doesn't already exist
            const updatedSkippedForms = formExists ? skippedForms : [...skippedForms, newSkippedForm];
            onSuccess({ skippedForms: updatedSkippedForms });
            return;
          }

          // Otherwise, show the error for the last form
          setFormDetail({
            form_id: currentForm.form_id,
            version: currentForm.version,
            original_version: currentForm.original_version || currentForm.version,
            schema: null,
            formData: null,
            submissionId: null,
            isLoading: false,
            error: errorMessage
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentFormData();

  }, [currentFormIndex, formsList, resolvedTokenData]);

  // Function to handle form submission from child components
  const handleFormSubmission = async (formData) => {
    // Ensure formData is an object even if null or undefined
    const safeFormData = formData || {};
    if (!resolvedTokenData) {
      setError('Authentication data is missing. Please try again.');
      return;
    }

    if (!formDetail) {
      setError('Form details are missing. Cannot submit.');
      return;
    }

    // Get patient ID from token - use 'sub' claim as patient_id
    const patientId = resolvedTokenData.sub;

    if (!patientId) {
      setError('Patient ID is missing. Please try again or contact support.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const formId = formDetail.form_id;
    const submissionId = formDetail.submissionId;
    const updateData = !!submissionId;
    const formVersion = formDetail.version || null; // Use version from formDetail

    try {
      const submitResponse = await patientApi.submitFormData(
        patientId,
        formId,
        safeFormData, // Use the data from the child component
        submissionId,
        updateData,
        formVersion,
        resolvedTokenData // Pass the resolved token data
      );

      if (submitResponse?.data?.success) {

        // Mark current form as completed
        setCompletedForms(prevCompleted => {
          const formKey = `${formId}-${formVersion}`;
          if (!prevCompleted.includes(formKey)) {
            return [...prevCompleted, formKey];
          }
          return prevCompleted;
        });

        // Update the submissionId in the local formDetail state
        setFormDetail(prevFormDetail => ({
          ...prevFormDetail,
          submissionId: submitResponse.data.submission_id || submissionId,
          formData: safeFormData // Update formData to reflect the submitted data
        }));

        // Move to the next form or call onSuccess if it's the last form
        if (currentFormIndex < formsList.length - 1) {
          const nextFormIndex = currentFormIndex + 1;
          const nextForm = formsList[nextFormIndex];
          setCurrentFormIndex(nextFormIndex);
          // Update URL to navigate to the next form, including version
          const newUrl = `?step=2&formid=${nextForm.form_id}${nextForm.original_version ? `&version=${nextForm.original_version}` : ''}`;
          navigate({
            search: newUrl
          });
          window.scrollTo(0, 0); // Scroll to top for the next form
        } else {
          // All forms submitted
          onSuccess({ skippedForms });
        }
      } else {
        setError(submitResponse?.data?.message || 'Failed to submit form data.');
      }
    } catch (submitError) {
      setError('An error occurred while submitting your form.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render component
  return (
    <div className="intake-forms-container">
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading form...</span>
          </div>
        </div>
      ) : (
        <>
          {error && <div className="alert alert-danger">{error}</div>}

          {!error && currentFormIndex === -1 && eligibleFormsList.length > 0 && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Forms to Complete</h5>
                <p className="card-text">Please complete the following forms:</p>
                <ul>
                  {eligibleFormsList.map((form, index) => (
                    <li key={form.form_id}>
                      Form ID: {form.form_id} (Version: {form.original_version || form.version || 'latest'})
                    </li>
                  ))}
                </ul>
                <button className="btn btn-primary mt-3" onClick={() => setCurrentFormIndex(0)}>
                  Start Forms
                </button>
              </div>
            </div>
          )}

          {!error && currentFormIndex !== -1 && formDetail ? (
            <>
              {/* Render the current form */}
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading form component...</span>
                  </div>
                </div>
              }>
                <FormContent
                  form={formDetail}
                  resolvedTokenData={resolvedTokenData}
                  handleFormSubmission={handleFormSubmission}
                  currentFormIndex={currentFormIndex}
                  totalForms={formsList.length}
                  formsList={formsList}
                  completedForms={completedForms}
                />
              </Suspense>

              {/* Navigation buttons - only show if form doesn't have an error */}
              {/* {!formDetail.error && currentFormIndex < formsList.length - 1 && (
                <div className="form-actions mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleFormSubmission(formDetail.formData || {})} // Submit current form data and move to next, use empty object if null
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      'Next Form'
                    )}
                  </button>
                </div>
              )} */}

              {!formDetail.error && currentFormIndex === formsList.length - 1 && (
                <div className="form-actions mt-3">
                  <button
                    className="btn btn-success"
                    onClick={() => handleFormSubmission(formDetail.formData || {})} // Submit final form, use empty object if null
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      'Complete'
                    )}
                  </button>
                </div>
              )}

              {/* Skip button for forms with errors */}
              {formDetail.error && (
                <div className="form-actions mt-3">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      // Move to next form or complete if this is the last form
                      if (currentFormIndex < formsList.length - 1) {
                        const nextFormIndex = currentFormIndex + 1;
                        const nextForm = formsList[nextFormIndex];
                        setCurrentFormIndex(nextFormIndex);
                        navigate({
                          search: `?step=2&formid=${nextForm.form_id}${nextForm.original_version ? `&version=${nextForm.original_version}` : ''}`
                        });
                      } else {
                        // This is the last form, complete the process
                        onSuccess({ skippedForms });
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    {currentFormIndex < formsList.length - 1 ? 'Skip to Next Form' : 'Continue to Next Step'}
                  </button>
                </div>
              )}
            </>
          ) : (
            // This block will now only render if not loading, no error, currentFormIndex is -1 or not -1 but no formDetail
            // The case for currentFormIndex === -1 and eligibleFormsList.length > 0 is handled above
            // So this will primarily handle the case where currentFormIndex !== -1 but formDetail is null (e.g., error fetching form)
            // or when there are no eligible forms at all.
            !isLoading && !error && currentFormIndex === -1 && eligibleFormsList.length === 0 && (
              <div className="alert alert-info">
                <h5>No Forms Assigned</h5>
                <p>There are no forms assigned for this check-in.</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default IntakeForms;
